/* Who is signed in, for the whole app.
 *
 * The old client kept this on a mutable `N` object that any of 5,600 lines
 * could reach and reassign — `N.token`, `N.user`, `N.date` — which is why
 * "am I signed in?" had four different answers depending on where you asked.
 * One context, one source of truth, and the compiler stops a component
 * reading `user.name` on a screen where there is no user.
 */

import { followAccountTheme } from './ui/theme';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from '@shared/types';
import { api, getToken, setToken, setSignedOutHandler } from './api';

interface SessionValue {
  user: User | null;
  /** The user's today, as the server computes it in their timezone. Never
      `new Date()` in the client: a plan is a local-date thing, and a browser
      an hour either side of midnight disagrees with the server about which
      day it is. */
  today: string | null;
  /** True until the first `/auth/me` settles. Rendering the sign-in screen
      before that resolves is the flash of a login form at every reload. */
  loading: boolean;
  signIn: (user: User, token: string, today?: string) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Today, in a given timezone. `en-CA` because it formats as YYYY-MM-DD, which
 *  is the shape every date on the wire uses.
 *
 *  This exists because the obvious fallback is wrong in a way nobody notices.
 *  `new Date().toISOString().slice(0, 10)` is the date in *UTC*, so anybody
 *  east of Greenwich in the evening gets tomorrow's plan and anybody west of
 *  it in the early morning gets yesterday's — silently, with a perfectly
 *  normal-looking screen. The server computes the day in the user's zone, and
 *  the client has to agree with it or the two are talking about different
 *  days. */
export function localToday(tz?: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC' }).format(new Date());
  } catch {
    /* An unknown zone must not throw on the way to rendering a screen. */
    return new Intl.DateTimeFormat('en-CA').format(new Date());
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [serverToday, setServerToday] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /* Ticks at local midnight so a day that rolls over while the app is open
     stops showing yesterday. A tab left open overnight used to keep asking
     for the previous day until it was reloaded. */
  const [midnightTick, setMidnightTick] = useState(0);

  const refresh = useCallback(async () => {
    if (!getToken()) { setUser(null); setLoading(false); return; }
    const r = await api<{ user: User; today: string }>('GET', '/api/auth/me');
    if (r.ok) {
      setUser(r.user);
      setServerToday(r.today);
      /* The account's own theme, when it has one — see followAccountTheme. */
      if (r.user && r.user.theme) followAccountTheme(r.user.theme);
    }
    else setUser(null);
    setLoading(false);
  }, []);

  const signIn = useCallback((next: User, token: string, day?: string) => {
    setToken(token);
    setUser(next);
    /* The sign-in response does not carry a date, and `refresh()` only runs on
       mount — so without deriving it here, `today` stayed null for the whole
       session after a fresh sign-in and every screen fell back to the
       browser's UTC date. Deriving it from the account's own timezone needs no
       round trip and agrees with the server by construction. */
    setServerToday(day ?? localToday(next.tz));
  }, []);

  const signOut = useCallback(async () => {
    /* Tell the server first so the session row is actually revoked, but never
       let a failed request strand somebody on a screen they wanted to leave —
       the local half happens either way. */
    try { await api('POST', '/api/auth/logout'); } catch { /* leaving anyway */ }
    setToken(null);
    setUser(null);
  }, []);

  /* A forced sign-out can arrive from any request, including a background
     refetch nobody clicked. Routing it through the same state as a deliberate
     sign-out means there is one way to become signed out, not two. */
  useEffect(() => {
    setSignedOutHandler(() => setUser(null));
    return () => setSignedOutHandler(() => {});
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  /* The server's answer is authoritative while it is fresh; after midnight has
     passed locally it is stale, and the account's own timezone is the better
     source. `midnightTick` is what makes this recompute at all. */
  const today = useMemo(() => {
    const local = localToday(user?.tz);
    void midnightTick;
    return serverToday && serverToday >= local ? serverToday : local;
  }, [serverToday, user?.tz, midnightTick]);

  useEffect(() => {
    if (!user) return;
    /* Wake up a few seconds after the next local midnight rather than polling.
       Capped at a day so a bad clock cannot schedule a timer months out. */
    const now = new Date();
    const next = new Date(now);
    next.setHours(24, 0, 5, 0);
    const ms = Math.min(Math.max(next.getTime() - now.getTime(), 1000), 86_400_000);
    const t = setTimeout(() => setMidnightTick(n => n + 1), ms);
    return () => clearTimeout(t);
  }, [user, midnightTick]);

  const value = useMemo<SessionValue>(
    () => ({ user, today, loading, signIn, signOut, refresh }),
    [user, today, loading, signIn, signOut, refresh]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession used outside SessionProvider');
  return ctx;
}

/** For screens that only exist behind a sign-in. Narrows `user` to non-null so
    every component below can read it without a guard. */
export function useUser(): User {
  const { user } = useSession();
  if (!user) throw new Error('useUser used outside a signed-in route');
  return user;
}
